import {
  Handshake,
  Building2,
  Network,
  Truck,
  ShoppingCart,
  Warehouse,
  CheckCircle,
  Beef,
  ArrowRight,
} from "lucide-react"
import Header from "@/components/Header"
import Footer from "@/components/Footer"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { useLanguage } from "@/hooks/useLanguage"

import { useAuth, UserType } from "@/hooks/useAuth"

const Services = () => {
  const { t } = useLanguage()
  const { user } = useAuth()
  const text = t.services

  const isSoftLaunch = import.meta.env.VITE_SOFT_LAUNCH === "true"
  const canAccessEcommerce = !isSoftLaunch || user?.userType === UserType.Admin

  const buyerBenefits = [
    {
      icon: Beef,
      title: text.directAccess,
      description: text.directAccessDesc,
      tag: "SUPPLY",
    },
    {
      icon: Network,
      title: text.variety,
      description: text.varietyDesc,
      tag: "NETWORK",
    },
    {
      icon: Handshake,
      title: text.convenience,
      description: text.convenienceDesc,
      tag: "MODELS",
    },
    {
      icon: Truck,
      title: text.quality,
      description: text.qualityDesc,
      tag: "LOGISTICS",
    },
    {
      icon: ShoppingCart,
      title: text.support,
      description: text.supportDesc,
      tag: "DIGITAL",
    },
  ]

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main>
        {/* Hero Section */}
        <section className="relative border-b-[1px] border-foreground/5 pb-32 pt-24">
          <div className="container mx-auto px-4">
            <div className="flex flex-col items-start justify-between gap-12 md:flex-row md:items-end">
              <div className="max-w-3xl space-y-6">
                <span className="text-xs font-black uppercase tracking-[0.4em] text-primary">
                  {text.subtitle}
                </span>
                <h1 className="font-display text-7xl uppercase leading-none tracking-tighter text-foreground md:text-9xl">
                  {text.heading}
                </h1>
                <p className="text-xl font-medium text-muted-foreground md:text-2xl">
                  {text.desc}
                </p>
              </div>
              <div className="hidden pb-4 lg:block">
                <div className="mx-auto h-32 w-px bg-foreground/20" />
              </div>
            </div>
          </div>
        </section>

        {/* Catalog Highlights */}
        <section className="bg-muted/30 py-32">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 gap-12 md:grid-cols-2">
              <div className="group relative space-y-8 border-[1px] border-foreground/10 bg-card p-12 shadow-2xl transition-all hover:bg-foreground hover:text-background">
                <div className="flex items-start justify-between">
                  <Warehouse className="h-12 w-12 text-primary transition-colors group-hover:text-primary" />
                  <span className="font-display text-xl opacity-20">01</span>
                </div>
                <h3 className="font-display text-5xl uppercase tracking-widest">
                  {text.industrialCat}
                </h3>
                {canAccessEcommerce && (
                  <Button
                    variant="ghost"
                    className="flex h-auto items-center gap-2 rounded-none border-b border-current p-0 pb-1 text-xs font-bold uppercase tracking-widest transition-colors hover:bg-transparent group-hover:text-primary"
                    onClick={() => (window.location.href = "/catalog/marketplace")}
                  >
                    {text.viewCatalog} <ArrowRight className="h-3 w-3" />
                  </Button>
                )}
              </div>

              <div className="group relative space-y-8 border-[1px] border-foreground/10 bg-card p-12 shadow-2xl transition-all hover:bg-foreground hover:text-background">
                <div className="flex items-start justify-between">
                  <Beef className="h-12 w-12 text-primary transition-colors group-hover:text-primary" />
                  <span className="font-display text-xl opacity-20">02</span>
                </div>
                <h3 className="font-display text-5xl uppercase tracking-widest">
                  {text.humanCat}
                </h3>
                {canAccessEcommerce && (
                  <Button
                    variant="ghost"
                    className="flex h-auto items-center gap-2 rounded-none border-b border-current p-0 pb-1 text-xs font-bold uppercase tracking-widest transition-colors hover:bg-transparent group-hover:text-primary"
                    onClick={() => (window.location.href = "/catalog/marketplace")}
                  >
                    {text.viewCatalog} <ArrowRight className="h-3 w-3" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Grid - Clean Industrial Lines */}
        <section className="py-32">
          <div className="container mx-auto px-4">
            <div className="mb-24 flex flex-col items-start justify-between border-b-[1px] border-foreground/5 pb-12 md:flex-row md:items-center">
              <h2 className="font-display text-6xl uppercase tracking-tighter">
                {text.buyerBenefits}
              </h2>
              <p className="mt-4 text-xs font-black uppercase tracking-[0.2em] text-muted-foreground md:mt-0">
                {text.coreServices}
              </p>
            </div>

            <div className="grid grid-cols-1 gap-x-12 gap-y-24 md:grid-cols-2 lg:grid-cols-3">
              {buyerBenefits.map((benefit, idx) => (
                <div key={idx} className="group space-y-6">
                  <div className="flex items-center justify-between border-b-[1px] border-foreground/10 pb-4">
                    <span className="text-xs font-black tracking-subtitle text-primary">
                      {benefit.tag}
                    </span>
                    <benefit.icon className="h-5 w-5 text-muted-foreground transition-colors group-hover:text-foreground" />
                  </div>
                  <h4 className="font-display text-2xl uppercase tracking-widest">
                    {benefit.title}
                  </h4>
                  <p className="font-medium text-muted-foreground">
                    {benefit.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Strategic Partnerships CTA */}
        <section className="relative overflow-hidden bg-foreground py-32 text-background dark:bg-[#121212] dark:text-foreground">
          <div className="absolute right-0 top-0 p-12 opacity-5">
            <Beef className="h-96 w-96" />
          </div>
          <div className="container relative z-10 mx-auto px-4">
            <div className="max-w-4xl space-y-12">
              <h2 className="font-display text-6xl uppercase leading-none tracking-tighter md:text-8xl">
                {text.ctaHeadline.split(" ").map((word, i) =>
                  word.toLowerCase() === "meat" || word.toLowerCase() === "cárnica" ? (
                    <span key={i} className="italic text-accent">
                      {word}{" "}
                    </span>
                  ) : (
                    word + " "
                  )
                )}
              </h2>
              <div className="flex flex-col gap-8 sm:flex-row">
                <Button
                  className="btn-industrial h-auto rounded-none bg-action px-12 py-6 text-2xl text-primary-foreground transition-all hover:scale-[1.02] active:scale-95"
                  onClick={() => (window.location.href = "/contact")}
                >
                  {text.partnerWithUs}
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  )
}

export default Services

